import { useDispatch, useSelector } from 'react-redux'
import { Fade } from 'components/misc/Fade'
import CircleSpinner from 'components/misc/CircleSpinner'
import Tooltip from 'components/misc/Tooltip'
import Header from 'components/typography/Header'
import SearchCites from './SearchCites'
import SettingsButton from './SettingsButton'
import { toggleFilter, setTextMatch, setSort, resetFilters } from 'store/report/actions'
import { MODAL_FROM_CONTEXTS, toggleModal } from 'store/ui/actions'
import styles from './styles/CitationsHeader.css'

import Classification from 'components/report/Classification'
import SortBox from '../misc/SortBox'
import {
  selectFilteredCitationPapers, selectFilteredCitationStatements,
  selectFilteredReferencePaperCount, selectFilteredReferenceStatementsCount,
  selectTotalReferenceStatementsCount
} from 'store/report/selectors'
import classNames from 'classnames'
import { SORT_OPTIONS_CITATIONS, SORT_OPTIONS_REFERENCES } from 'lib/helpers/sort-citation'
import Link from '../misc/Link'
import { userIsAtLeast } from '../../lib/user'
import { PLANS } from '../../../server/lib/constants'
import { setMode } from '../../store/login-modal/actions'

const ReferencesCount = ({
  referencesCount,
  onClick,
  referencesStatementsCount,
  showReferences,
  loading
}) => (
  <div
    tabIndex={0}
    role='button'
    className={classNames(styles.referencesCount, {
      [styles.selected]: showReferences
    })}
    onClick={onClick}
  >
    <Tooltip
      placement='top'
      content={['See references used by this publication.', ' Lower than you expected? This list of references comes from metadata supplied by Crossref through the Initiative for Open Citations, or through our automated PDF extraction; it can sometimes deviate from the actual number of references used. Only documents with DOIs are included in the counts.']}
      refClassName={styles.imageTooltip}
    >
      <img
        className={styles.citationsImg}
        alt='references'
        src={`${CONFIG.assetsBaseURL}/images/icons/references-icon.svg`}
        height='32px'
        width='32px'
      />
      {loading
        ? (
          <div className={styles.loadingHeader}>
            <CircleSpinner size={32} />
          </div>
          )
        : (
          <>
            <Header className={styles.citeHeader} level={5}>
              References {referencesCount?.toLocaleString() || 0} publication{referencesCount === 1 ? '' : 's'}
            </Header>
            {referencesStatementsCount > 0 && <div className={styles.citeHeaderLabel}>({referencesStatementsCount?.toLocaleString()} reference statement{referencesStatementsCount === 1 ? '' : 's'})</div>}
          </>
          )}
    </Tooltip>
  </div>
)

const CiteCount = ({
  citationStatementsCount,
  distinctSourceCount,
  resetFilters,
  citationsSelected,
  onClick,
  showReferences,
  hasReferences
}) => (
  <div
    className={classNames(styles.citeCount, {
      [styles.selected]: !showReferences
    })}
    onClick={onClick}
    tabIndex={0}
    role='button'
  >
    {citationsSelected
      ? (
        <>
          <Tooltip
            placement='top'
            content='See citation contexts from citing publications.'
            refClassName={styles.imageTooltip}
          >
            <img
              className={styles.citationsImg}
              alt='citations'
              src={`${CONFIG.assetsBaseURL}/images/icons/citations-icon.svg`}
              height='32px'
              width='32px'
            />
            <Header className={styles.citeHeader} level={5}>
              Selected {distinctSourceCount?.toLocaleString() || 0} publication{distinctSourceCount === 1 ? '' : 's'}
            </Header>
            <div className={styles.citeHeaderLabel}>(<Link onClick={() => resetFilters()} blue underline>click here to deselect</Link>)</div>
          </Tooltip>
        </>
        )
      : (
        <>
          <Tooltip
            placement='top'
            content={['See citation contexts from citing publications.', ' Lower than you expected? Citation statements come from full-text articles that we have indexed through publisher agreements. In some instances we can have gaps in coverage for citation statements, although this is improving every day. In such cases, we still have metadata about the citing publications without the citation statements. Only publications with DOIs are included.']}
            refClassName={styles.imageTooltip}
          >
            <img
              className={styles.citationsImg}
              alt='citations'
              src={`${CONFIG.assetsBaseURL}/images/icons/citations-icon.svg`}
              height='32px'
              width='32px'
            />
            <Header className={styles.citeHeader} level={5}>
              Cited by {distinctSourceCount?.toLocaleString() || 0} publication{distinctSourceCount === 1 ? '' : 's'}
            </Header>
            {citationStatementsCount > 0 && <div className={styles.citeHeaderLabel}>{' '}({citationStatementsCount?.toLocaleString()} citation statement{citationStatementsCount > 1 ? 's' : ''})</div>}
          </Tooltip>
        </>
        )}
    <Fade show={citationStatementsCount === 0}>
      <Tooltip
        className={styles.countDisclaimer}
        header='Does this number seem low?'
        content="In order to extract citation statements, we need to process full-text articles whereas other citation indices do not. While this means our numbers might be a bit lower, we're adding millions of citation statements to the database each day!"
      >
        <i className={styles.countDisclaimerIcon} />
      </Tooltip>
    </Fade>
  </div>
)

const CitationsHeader = () => {
  const dispatch = useDispatch()
  const user = useSelector(({ ui }) => ui.user)
  const referralPartner = useSelector(({ ui }) => ui.referralPartner)

  const restrictedCitationCount = useSelector(({ report }) => report.metadata?.restrictedCitationCount)
  const totalCitationStatementsCount = useSelector(({ report }) => report.metadata?.totalCitationCount)
  const textMatch = useSelector(({ report }) => report.filters.textMatch)
  const sort = useSelector(({ report }) => report.filters.sort)
  const exampleReport = useSelector(({ report }) => CONFIG.exampleDOIs.includes(report.paper))
  const citationIDs = useSelector(({ report }) => report.filters.citationIDs)
  const sourceDOIIDs = useSelector(({ report }) => report.filters.sourceDOIIDs)
  const citationsSelected = citationIDs.length > 0 || sourceDOIIDs.length > 0

  const filteredDistinctPapersCount = useSelector(({ report }) => selectFilteredCitationPapers(report))
  const filteredCitationStatementsCount = useSelector(({ report }) => selectFilteredCitationStatements(report))
  const totalSourcesCount = useSelector(({ report }) => report.metadata?.distinctSourceCount || Object.keys(report.papers).length - 1)
  const filteredReferencesCount = useSelector(({ report }) => selectFilteredReferencePaperCount(report))
  const filteredReferencesStatementsCount = useSelector(({ report }) => selectFilteredReferenceStatementsCount(report))
  const totalReferencesCount = useSelector(({ report }) => report.referencesTotalCount)
  const totalReferenceStatementsCount = useSelector(({ report }) => report.metadata?.totalReferenceStatementsCount || selectTotalReferenceStatementsCount(report))

  const showReferences = useSelector(({ report }) => report.filters.showReferences)
  const referencesLoadState = useSelector(({ report }) => report.referencesLoadTracker.state)

  return (
    <div className={styles.citationsSort}>
      <div className={styles.citationsHeader}>
        <CiteCount
          citationsSelected={citationsSelected}
          resetFilters={() => dispatch(resetFilters())}
          citationStatementsCount={(user || exampleReport) && !showReferences ? filteredCitationStatementsCount : totalCitationStatementsCount}
          distinctSourceCount={(user || exampleReport) && !showReferences ? filteredDistinctPapersCount : totalSourcesCount}
          onClick={() => {
            dispatch(resetFilters())
            if (showReferences) {
              dispatch(toggleFilter('showReferences', false, { prune: true }))
            }
          }}
          showReferences={showReferences}
          hasReferences={totalReferencesCount > 0}
        />
        <ReferencesCount
          referencesStatementsCount={showReferences ? filteredReferencesStatementsCount : totalReferenceStatementsCount}
          referencesCount={showReferences ? filteredReferencesCount : totalReferencesCount}
          showReferences={showReferences}
          loading={referencesLoadState === 'loading'}
          onClick={() => {
            if (!user && !exampleReport && !referralPartner) {
              dispatch(setMode('register'))
              dispatch(toggleModal('login', true, MODAL_FROM_CONTEXTS.REFERENCES))
              return
            }
            if (!userIsAtLeast(user, PLANS.premium)) {
              dispatch(toggleModal('upgrade', true, MODAL_FROM_CONTEXTS.REFERENCESN))
              return
            }
            dispatch(resetFilters())
            if (!showReferences) {
              dispatch(toggleFilter('showReferences', true, { prune: true }))
            }
          }}
        />
      </div>
      <div className={styles.mobileFilters}>
        <Classification className={styles.mobileClassificationfilter} />
        <div className={styles.searchCitesMobileLayout}>
          <SearchCites
            upgradeReport={!!user}
            fullReport={!restrictedCitationCount}
            toggleModal={(name, to, from) => dispatch(toggleModal(name, to, from))}
            textMatch={textMatch}
            setTextMatch={(text, debounce) => dispatch(setTextMatch(text, debounce))}
          />
        </div>
        <div className={styles.sortCitesMobileLayout}>
          <SortBox
            options={showReferences ? SORT_OPTIONS_REFERENCES : SORT_OPTIONS_CITATIONS}
            sort={sort}
            setSort={sort => dispatch(setSort(sort))}
          />
        </div>
      </div>
      <div className={styles.settingsButtonLayout}>
        <SettingsButton toggle={() => dispatch(toggleFilter('showMobile'))} />
      </div>
    </div>
  )
}

export default CitationsHeader
